import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AritoHeaderTabs, AritoForm } from '@/components/custom/erp';
import { calculateTotals, transformFormData } from '../../utils';
import { FormSchema, initialFormValues } from '../../schema';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { useDetailRows, useTaxRows } from './hooks';
import { useAuth } from '@/contexts/auth-context';
import { QuyenChungTu } from '@/types/schemas';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { FormMode } from '@/types/form';
import { BottomBar } from './BottomBar';
import { useNgoaiTe } from '@/hooks';
import DetailTab from './DetailTab';
import FileTab from './FileTab';
import TaxTab from './TaxTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
}

const BottomBarWrapper = ({ detailRows, exchangeRate = 1 }: { detailRows: any[]; exchangeRate?: number }) => {
  const { setValue } = useFormContext();

  const totals = useMemo(() => {
    return calculateTotals(detailRows, exchangeRate);
  }, [detailRows, exchangeRate]);

  useEffect(() => {
    setValue('t_ps_nt', totals.totalAmountNT);
    setValue('t_ps', totals.totalAmount);
  }, [totals, setValue]);

  return <BottomBar totalAmount={totals.totalAmount} totalAmountNT={totals.totalAmountNT} />;
};

const CurrencyTracker = ({ children }: { children: (currencyInfo: any, exchangeRate: number) => React.ReactNode }) => {
  const { watch } = useFormContext();
  const { currencies } = useNgoaiTe();

  const selectedCurrencyUuid = watch('ma_nt');
  const exchangeRate = watch('ty_gia') || 1;

  const currencyInfo = useMemo(() => {
    if (!selectedCurrencyUuid || !currencies.length) return null;
    return currencies.find(c => c.uuid === selectedCurrencyUuid) || null;
  }, [selectedCurrencyUuid, currencies]);

  return <>{children(currencyInfo, Number(exchangeRate))}</>;
};

const FormDialog = ({ open, onClose, onSubmit, formMode, initialData }: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);

  const [quyenChungTu, setQuyenChungTu] = useState<QuyenChungTu | null>(initialData?.ma_nk_data || null);
  const [soChungTu, setSoChungTu] = useState<string>(initialData?.so_ct || '');
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: selectedDetailRowUuid,
    selectedCell: selectedDetailCell,
    handleRowClick: handleDetailRowClick,
    handleCellClick: handleDetailCellClick,
    handleAddRow: handleDetailAddRow,
    handleDeleteRow: handleDetailDeleteRow,
    handleCopyRow: handleDetailCopyRow,
    handlePasteRow: handleDetailPasteRow,
    handleMoveRow: handleDetailMoveRow,
    handleCellValueChange: handleDetailCellValueChange,
    setNonWritableFields: setDetailNonWritableFields,
    setPasteConfigs: setDetailPasteConfigs,
    isPasting: isDetailPasting
  } = useDetailRows(initialData?.chi_tiet_data || []);
  const {
    rows: taxRows,
    setRows: setTaxRows,
    selectedRowUuid: selectedTaxRowUuid,
    selectedCell: selectedTaxCell,
    handleRowClick: handleTaxRowClick,
    handleCellClick: handleTaxCellClick,
    handleAddRow: handleTaxAddRow,
    handleDeleteRow: handleTaxDeleteRow,
    handleCopyRow: handleTaxCopyRow,
    handlePasteRow: handleTaxPasteRow,
    handleMoveRow: handleTaxMoveRow,
    handleCellValueChange: handleTaxCellValueChange,
    setNonWritableFields: setTaxNonWritableFields,
    setPasteConfigs: setTaxPasteConfigs,
    isPasting: isTaxPasting
  } = useTaxRows(initialData?.thong_tin_thue_data || []);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows,
    markDirty: () => setIsFormDirty(true)
  });
  const handleRepeatTaxRows = useRepeatRowsHandler({
    setRows: setTaxRows,
    markDirty: () => setIsFormDirty(true)
  });

  const totals = useMemo(() => {
    return calculateTotals(detailRows);
  }, [detailRows]);

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
    }
  }, [open]);

  const handleSubmit = async (data: any) => {
    const dataWithTotals = {
      ...data,
      t_ps_nt: totals.totalAmountNT,
      t_ps: totals.totalAmount
    };

    const transformedData = transformFormData(
      dataWithTotals,
      { quyenChungTu, soChungTu },
      detailRows,
      taxRows,
      entityUnit,
      formMode,
      initialData
    );

    try {
      await onSubmit(transformedData);

      setIsFormDirty(false);
    } catch (error: any) {
      console.error('Submit failed:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <AritoForm
        mode={formMode}
        hasAritoActionBar={true}
        schema={FormSchema}
        onSubmit={handleSubmit}
        onClose={handleClose}
        initialData={initialData || initialFormValues}
        title={formMode === 'add' ? 'Mới' : 'Sửa'}
        subTitle='Phiếu kế toán theo nghiệp vụ'
        className='w-full overflow-y-auto'
        headerFields={
          <AritoHeaderTabs
            tabs={[
              {
                id: 'basic-info',
                label: 'Thông tin',
                component: (
                  <div onChange={() => setIsFormDirty(true)}>
                    <BasicInfoTab
                      formMode={formMode}
                      quyenChungTu={quyenChungTu}
                      setQuyenChungTu={setQuyenChungTu}
                      soChungTu={soChungTu}
                      setSoChungTu={setSoChungTu}
                    />
                  </div>
                )
              }
            ]}
            actionButtons={
              formMode !== 'add' && initialData?.uuid ? (
                <InfoDropdown uuid={initialData.uuid} ma_ct={initialData?.ma_nk_data?.ma_ct} />
              ) : undefined
            }
          />
        }
        tabs={[
          {
            id: 'detail',
            label: 'Chi tiết',
            component: (
              <CurrencyTracker>
                {(currencyInfo, exchangeRate) => (
                  <DetailTab
                    mode={formMode}
                    rows={detailRows}
                    selectedRowUuid={selectedDetailRowUuid}
                    selectedCell={selectedDetailCell}
                    onRowClick={handleDetailRowClick}
                    onCellClick={handleDetailCellClick}
                    onAddRow={handleDetailAddRow}
                    onDeleteRow={handleDetailDeleteRow}
                    onCopyRow={handleDetailCopyRow}
                    onPasteRow={handleDetailPasteRow}
                    onMoveRow={handleDetailMoveRow}
                    onRepeatForRows={handleRepeatDetailRows}
                    onExport={() => console.log('Export clicked')}
                    onPin={() => console.log('Pin clicked')}
                    onCellValueChange={handleDetailCellValueChange}
                    currencyInfo={currencyInfo}
                    exchangeRate={exchangeRate}
                    handleUpdateNonWritableFields={setDetailNonWritableFields}
                    handleUpdatePasteConfigs={setDetailPasteConfigs}
                    isPasting={isDetailPasting}
                  />
                )}
              </CurrencyTracker>
            )
          },
          {
            id: 'tax',
            label: 'Thuế',
            component: (
              <TaxTab
                mode={formMode}
                rows={taxRows}
                selectedRowUuid={selectedTaxRowUuid}
                selectedCell={selectedTaxCell}
                onRowClick={handleTaxRowClick}
                onCellClick={handleTaxCellClick}
                onAddRow={handleTaxAddRow}
                onDeleteRow={handleTaxDeleteRow}
                onCopyRow={handleTaxCopyRow}
                onPasteRow={handleTaxPasteRow}
                onMoveRow={handleTaxMoveRow}
                onRepeatForRows={handleRepeatTaxRows}
                onExport={() => console.log('Export clicked')}
                onPin={() => console.log('Pin clicked')}
                onCellValueChange={handleTaxCellValueChange}
                handleUpdateNonWritableFields={setTaxNonWritableFields}
                handleUpdatePasteConfigs={setTaxPasteConfigs}
                isPasting={isTaxPasting}
              />
            )
          },
          {
            id: 'file',
            label: 'File đính kèm',
            component: <FileTab formMode={formMode} />
          }
        ]}
        bottomBar={
          <CurrencyTracker>
            {(currencyInfo, exchangeRate) => <BottomBarWrapper detailRows={detailRows} exchangeRate={exchangeRate} />}
          </CurrencyTracker>
        }
      />
    </>
  );
};

export default FormDialog;
